import { useEffect, useRef, useState } from 'react';
import { ABOUT } from '@/data/about';
import { clamp, subscribeScroll, type ScrollState } from '@components/motion';
import FigmaFrame from '@components/landing/FigmaFrame';

const MISSION_IMAGE = '/about/mission-image.webp';
const VISION_IMAGE = '/about/team-portrait.webp';

/** Copy for each panel of the pinned Mission → Vision scene. */
/** The two panels of the pinned scene. */
type PanelLabel = 'Mission' | 'Vision';

function Heading({ label, className }: { label: PanelLabel; className?: string }) {
    return (
        <div className={`font-primary font-semibold capitalize leading-none tracking-[-3.3px] ${className ?? ''}`}>
            <p className="mb-0 text-[#a0a3a5]">Our</p>
            <p className="text-[#08080c]">{label}</p>
        </div>
    );
}

function Body({ label, className }: { label: PanelLabel; className?: string }) {
    return (
        <div className={`space-y-4 text-[16px] font-medium leading-[1.8] text-[#282828] ${className ?? ''}`}>
            {(label === 'Mission' ? ABOUT.missionVision.missionParagraphs : ABOUT.missionVision.visionParagraphs).map(
                (p) => (
                    <p key={p}>{p}</p>
                ),
            )}
        </div>
    );
}

/* Pinned-scene geometry (design px inside the 1920-wide frame). */
const PANEL_H = 720;
const FRAME_H = 900;

function MissionVisionDesktop() {
    const sceneRef = useRef<HTMLElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const [isVision, setIsVision] = useState(false);
    const visionRef = useRef(false);

    // While the scene is pinned, scroll scrubs the text track upward past the
    // stationary image; crossing the midpoint swaps the image to the Vision one.
    useEffect(() => {
        const scene = sceneRef.current;
        const track = trackRef.current;
        if (!scene || !track) return;
        const apply = (state: ScrollState): void => {
            const rect = scene.getBoundingClientRect();
            const distance = rect.height - state.viewportHeight;
            const p = distance > 0 ? clamp(-rect.top / distance) : 0;
            track.style.transform = `translateY(${-p * PANEL_H}px)`;
            const vision = p > 0.5;
            if (vision !== visionRef.current) {
                visionRef.current = vision;
                setIsVision(vision);
            }
        };
        return subscribeScroll(apply);
    }, []);

    return (
        <section ref={sceneRef} className="relative bg-[#efefef]" style={{ height: '230vh' }}>
            <div className="sticky top-0 flex h-dvh items-center overflow-hidden">
                <FigmaFrame designWidth={1920} designHeight={FRAME_H} className="w-full">
                    <div className="relative w-[1920px] bg-[#efefef]" style={{ height: FRAME_H }}>
                        {/* Image — stays put; cross-fades Mission → Vision */}
                        <div className="absolute left-[143px] top-[90px] h-[720px] w-[484px] overflow-hidden rounded-[24px]">
                            <img
                                src={ABOUT.missionVision.missionImage || MISSION_IMAGE}
                                alt={ABOUT.missionVision.missionImage ? ABOUT.missionVision.missionImageAlt : ''}
                                className={`absolute inset-0 size-full object-cover transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                    isVision ? 'opacity-0' : 'opacity-100'
                                }`}
                            />
                            <img
                                src={ABOUT.missionVision.visionImage || VISION_IMAGE}
                                alt={ABOUT.missionVision.visionImage ? ABOUT.missionVision.visionImageAlt : ''}
                                className={`absolute inset-0 size-full object-cover transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                    isVision ? 'opacity-100' : 'opacity-0'
                                }`}
                            />
                            <div className="absolute inset-0 bg-black/20" />
                        </div>

                        {/* Text window — the track slides up inside it */}
                        <div
                            className="absolute left-[872px] top-[90px] w-[906px] overflow-hidden"
                            style={{ height: PANEL_H }}
                        >
                            <div ref={trackRef} className="will-change-transform">
                                <div className="relative" style={{ height: PANEL_H }}>
                                    <Heading
                                        label="Mission"
                                        className="absolute left-0 top-[40px] w-[753px] text-[110px]"
                                    />
                                    <Body label="Mission" className="absolute left-0 top-[315px] w-[906px]" />
                                </div>
                                <div className="relative" style={{ height: PANEL_H }}>
                                    <Heading
                                        label="Vision"
                                        className="absolute left-0 top-[40px] w-[753px] text-[110px]"
                                    />
                                    <Body label="Vision" className="absolute left-0 top-[315px] w-[906px]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </FigmaFrame>
            </div>
        </section>
    );
}

function MissionVisionMobile() {
    return (
        <section className="bg-[#efefef] px-5 py-16">
            <div className="relative h-[320px] w-full overflow-hidden rounded-[20px]">
                <img
                    src={ABOUT.missionVision.missionImage || MISSION_IMAGE}
                    alt={ABOUT.missionVision.missionImage ? ABOUT.missionVision.missionImageAlt : ''}
                    className="absolute inset-0 size-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20" />
            </div>
            <Heading label="Mission" className="mt-10 text-[56px]" />
            <Body label="Mission" className="mt-5 text-[14px]" />

            <Heading label="Vision" className="mt-14 text-[56px]" />
            <Body label="Vision" className="mt-5 text-[14px]" />
        </section>
    );
}

export default function AboutMissionVision() {
    return (
        <>
            <div className="hidden lg:block">
                <MissionVisionDesktop />
            </div>
            <div className="lg:hidden">
                <MissionVisionMobile />
            </div>
        </>
    );
}
